import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Search, Filter, Loader2, SlidersHorizontal, MoreHorizontal, Info, Users, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { AddClientPanel } from "@/components/AddClientPanel";
import { getExchange } from "@/data/exchanges";
import { fmtCompactUsd, fmtDate } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

function ClientAvatar({ slug }) {
  const ex = getExchange(slug);
  return (
    <div className="relative flex-shrink-0" data-testid="client-avatar">
      <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#333333] flex items-center justify-center overflow-hidden">
        <Users className="w-5 h-5 text-[#737373]" />
      </div>
      <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#0D0D0D] border-2 border-[#141414] flex items-center justify-center font-heading font-bold text-[9px]" style={{ color: ex.color }}>
        {ex.name.slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

export default function MyClients() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["partner-clients", statusFilter],
    queryFn: async () => (await api.get("/partner/clients", { params: statusFilter ? { status: statusFilter } : {} })).data,
  });

  const filtered = q.trim()
    ? clients.filter((c) => c.account_name?.toLowerCase().includes(q.toLowerCase()) || c.exchange_name?.toLowerCase().includes(q.toLowerCase()))
    : clients;

  const del = async (id) => {
    try {
      await api.delete(`/partner/clients/${id}`);
      qc.invalidateQueries({ queryKey: ["partner-clients"] });
      toast.success("Client removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="My Clients"
        pageKey="partner-clients"
        testid="my-clients-header"
      />

      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#525252]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." data-testid="client-search"
              className="w-64 bg-[#0A0A0A] border border-[#333333] rounded-md pl-9 pr-3 py-2 text-sm text-[#F5F5F5] placeholder:text-[#525252] focus:outline-none focus:border-[#D4AF37]" />
          </div>
          <button onClick={() => setStatusFilter((s) => (s === "active" ? "" : "active"))} data-testid="client-filter"
            className={cn("w-9 h-9 rounded-md border flex items-center justify-center transition-colors",
              statusFilter ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#333333] text-[#A3A3A3] hover:text-[#F5F5F5]")}>
            <Filter className="w-4 h-4" />
          </button>
        </div>
        <button onClick={() => setAddOpen(true)} data-testid="add-account-btn"
          className="flex items-center gap-2 bg-[#D4AF37] hover:bg-[#F0C74B] text-[#0A0A0A] font-semibold text-sm rounded-md px-4 py-2 transition-colors">
          <span className="w-4 h-4 rounded-full bg-[#0A0A0A]/20 flex items-center justify-center"><Plus className="w-3 h-3" /></span>
          Add Account
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#333333] bg-[#0D0D0D] p-16 text-center" data-testid="clients-empty">
          <Users className="w-8 h-8 text-[#525252] mx-auto mb-3" />
          <div className="text-[#F5F5F5] font-medium">No clients yet</div>
          <p className="text-sm text-[#737373] mt-1">Connect your first client account to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="rounded-lg border border-[#1f1f1f] bg-[#0D0D0D] px-5 py-4 flex items-center gap-4 hover:border-[#333333] transition-colors"
              data-testid={`client-row-${c.id}`}
            >
              <ClientAvatar slug={c.exchange_slug} />
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-[#F5F5F5] truncate">{c.account_name}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm">{c.connected ? "Connected" : "Disconnected"}</span>
                  <span className="text-[10px] uppercase tracking-wide text-[#A3A3A3] bg-[#1A1A1A] border border-[#262626] px-2 py-0.5 rounded-sm capitalize">{c.market}</span>
                </div>
              </div>

              <div className="hidden md:block text-center w-20">
                <div className="text-[10px] uppercase tracking-wide text-[#737373]">Status</div>
                <div className={cn("text-sm font-semibold mt-1", c.status === "active" ? "text-[#10B981]" : "text-[#737373]")}>{c.status === "active" ? "Active" : "Inactive"}</div>
              </div>
              <div className="hidden md:block text-center w-24">
                <div className="text-[10px] uppercase tracking-wide text-[#737373]">Balance</div>
                <div className="text-sm font-mono font-semibold text-[#F5F5F5] mt-1">{fmtCompactUsd(c.balance)}</div>
              </div>
              <div className="hidden lg:block text-center w-32">
                <div className="text-[10px] uppercase tracking-wide text-[#737373] flex items-center justify-center gap-1">Updated <Info className="w-3 h-3" /></div>
                <div className="text-sm text-[#F5F5F5] mt-1">{fmtDate(c.updated_at).replace(",", "")}</div>
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => navigate(`/partner/clients/${c.id}`)} data-testid={`manage-client-${c.id}`}
                  className="flex items-center gap-2 border border-[#333333] text-[#F5F5F5] text-sm rounded-md px-3 py-1.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Manage
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`client-menu-${c.id}`} className="w-8 h-8 rounded-md border border-[#333333] text-[#A3A3A3] flex items-center justify-center hover:text-[#F5F5F5] transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-[#F5F5F5]">
                    <DropdownMenuItem onClick={() => navigate(`/partner/clients/${c.id}`)} className="cursor-pointer focus:bg-[#1A1A1A]">
                      <SlidersHorizontal className="w-4 h-4 mr-2" /> Manage
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => del(c.id)} data-testid={`client-delete-${c.id}`} className="cursor-pointer text-[#EF4444] focus:bg-[rgba(239,68,68,0.1)] focus:text-[#EF4444]">
                      <Trash2 className="w-4 h-4 mr-2" /> Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AddClientPanel open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
